import * as moment from 'moment';

export interface IValidationResult {
    isValid: boolean,
    error: string
}

export const MIN_DAYS_BEFORE_SURGERY = 5;
export const MAX_DAYS_BEFORE_SURGERY = 7;
export const FINAL_XM_BUSINESS_DAYS_AFTER_REQUEST = 3;
export const SEROLOGY_BUSINESS_DAYS_AFTER_REQUEST = 3;
export const FINAL_XM_MIN_DAYS_BEFORE_SURGERY = 5;
export const PRE_OP_SEROLOGY_BUSINESS_DAYS_AFTER_REQUEST = 3;
export const RELAXED_GATE_DAYS = 7;
export const RELAXED_MIN_DAYS_BEFORE_SURGERY = 2;
export const STORAGE_FORMAT = 'YYYY-MM-DD';
export const DISPLAY_FORMAT = 'MM/DD/YYYY';

const DATE_SCAN_LIMIT_DAYS = 60;
const FRIDAY = 5;

export const isWeekend = (date: moment.Moment): boolean => {
    const dayOfWeek = date.day();

    return dayOfWeek === 0 || dayOfWeek === 6;
};

export const addBusinessDays = (from: moment.Moment, businessDays: number): moment.Moment => {
    const result = from.clone().startOf('day');
    let added = 0;

    while (added < businessDays) {
        result.add(1, 'day');

        if (!isWeekend(result)) {
            added++;
        }
    }

    return result;
};

// Surgery inside the gate replaces the 5-7 window with tomorrow through surgery - 2, for both
// draws. The two paths are contiguous: outside the gate surgery is 8+ days away, so surgery - 7
// is tomorrow or later and today is never legal on either path.
export const isRelaxedWindow = (targetSurgeryDate: string): boolean => {
    if (!targetSurgeryDate) {
        return false;
    }

    return moment(targetSurgeryDate).startOf('day').diff(moment().startOf('day'), 'days') <= RELAXED_GATE_DAYS;
};

// allowToday exists for the Action Center alert, which surfaces draws scheduled from today onward
// and must let a coordinator confirm one that is happening today. The XM accept modal schedules a
// future draw, so it keeps the tomorrow-or-later floor. Only the floor differs - the 5-7 window and
// the relaxed gate below are shared, and they are what drifted when this logic was duplicated.
export const validateSerologyDate = (date: moment.Moment, targetSurgeryDate: string, allowToday: boolean = false, superAdminOverride: boolean = false): IValidationResult => {
    const day = date.clone().startOf('day');
    const earliestAllowed = allowToday ? moment().startOf('day') : moment().startOf('day').add(1, 'day');

    if (isWeekend(day)) {
        return { isValid: false, error: 'Serology blood draw cannot fall on a weekend.' };
    }

    if (day.isBefore(earliestAllowed, 'day')) {
        return {
            isValid: false,
            error: allowToday
                ? 'Serology blood draw cannot be in the past.'
                : 'Serology blood draw must be tomorrow or later.'
        };
    }

    if (!targetSurgeryDate) {
        return { isValid: true, error: '' };
    }

    const surgeryDate = moment(targetSurgeryDate).startOf('day');

    if (day.isSameOrAfter(surgeryDate, 'day')) {
        return { isValid: false, error: 'Serology blood draw must be before the donor surgery date.'};
    }

    if (isRelaxedWindow(targetSurgeryDate)) {
        const relaxedLatest = surgeryDate.clone().subtract(RELAXED_MIN_DAYS_BEFORE_SURGERY, 'days');

        if (day.isAfter(relaxedLatest, 'day')) {
            return { isValid: false, error: `Surgery is within ${RELAXED_GATE_DAYS} days, so the blood draw must be between ${earliestAllowed.format(DISPLAY_FORMAT)} and ${relaxedLatest.format(DISPLAY_FORMAT)}.` };
        }

        return { isValid: true, error: '' };
    }

    if (!superAdminOverride) {
        const leadTimeEarliest = addBusinessDays(moment(), SEROLOGY_BUSINESS_DAYS_AFTER_REQUEST);

        if (day.isBefore(leadTimeEarliest, 'day')) {
            return { isValid: false, error: `Serology blood draw must be at least ${SEROLOGY_BUSINESS_DAYS_AFTER_REQUEST} business days from today (on or after ${leadTimeEarliest.format(DISPLAY_FORMAT)}).` };
        }
    }

    const daysBeforeSurgery = surgeryDate.diff(day, 'days');

    if (daysBeforeSurgery < MIN_DAYS_BEFORE_SURGERY || daysBeforeSurgery > MAX_DAYS_BEFORE_SURGERY) {
        const earliest = surgeryDate.clone().subtract(MAX_DAYS_BEFORE_SURGERY, 'days');
        const latest = surgeryDate.clone().subtract(MIN_DAYS_BEFORE_SURGERY, 'days');

        return { isValid: false, error: `Serology blood draw must be ${MIN_DAYS_BEFORE_SURGERY}-${MAX_DAYS_BEFORE_SURGERY} days before surgery (${earliest.format(DISPLAY_FORMAT)}-${latest.format(DISPLAY_FORMAT)}).` };
    }

    return { isValid: true, error: '' };
};

export const validateFinalXmDate = (date: moment.Moment, targetSurgeryDate: string, superAdminOverride: boolean = false): IValidationResult => {
    const day = date.clone().startOf('day');
    const dayOfWeek = day.day();

    if (dayOfWeek < 1 || dayOfWeek > 4) {
        return { isValid: false, error: 'Final XM blood draw must be Monday to Thursday.' };
    }

    if (isRelaxedWindow(targetSurgeryDate)) {
        const tomorrow = moment().startOf('day').add(1, 'day');
        const relaxedLatest = moment(targetSurgeryDate).startOf('day').subtract(RELAXED_MIN_DAYS_BEFORE_SURGERY, 'days');

        if (day.isBefore(tomorrow, 'day') || day.isAfter(relaxedLatest, 'day')) {
            return { isValid: false, error: `Surgery is within ${RELAXED_GATE_DAYS} days, so the blood draw must be between ${tomorrow.format(DISPLAY_FORMAT)} and ${relaxedLatest.format(DISPLAY_FORMAT)}.` };
        }

        return { isValid: true, error: '' };
    }

    if (!superAdminOverride) {
        const earliest = addBusinessDays(moment(), FINAL_XM_BUSINESS_DAYS_AFTER_REQUEST);

        if (day.isBefore(earliest, 'day')) {
            return { isValid: false, error: `Final XM blood draw must be at least ${FINAL_XM_BUSINESS_DAYS_AFTER_REQUEST} business days from today (on or after ${earliest.format(DISPLAY_FORMAT)}).` };
        }
    }

    if (!targetSurgeryDate) {
        return { isValid: true, error: '' };
    }

    const latest = moment(targetSurgeryDate).startOf('day').subtract(FINAL_XM_MIN_DAYS_BEFORE_SURGERY, 'days');

    if (day.isAfter(latest, 'day')) {
        return { isValid: false, error: `Final XM blood draw must be at least ${FINAL_XM_MIN_DAYS_BEFORE_SURGERY} days before surgery (on or before ${latest.format(DISPLAY_FORMAT)}).` };
    }

    return { isValid: true, error: '' };
};

// Seeds come from the validator itself rather than a parallel formula, so a default can never
// be a date its own field then rejects. Returns '' when the scan finds nothing legal.
const firstAcceptableDate = (start: moment.Moment, isAcceptable: (date: moment.Moment) => boolean): string => {
    const candidate = start.clone().startOf('day');

    for (let i = 0; i < DATE_SCAN_LIMIT_DAYS; i++) {
        if (isAcceptable(candidate)) {
            return candidate.format(STORAGE_FORMAT);
        }

        candidate.add(1, 'day');
    }

    return '';
};

export const defaultSerologyDate = (targetSurgeryDate: string): string => {
    const tomorrow = moment().startOf('day').add(1, 'day');
    let start = tomorrow;

    if (targetSurgeryDate) {
        const windowOpens = moment(targetSurgeryDate).startOf('day').subtract(MAX_DAYS_BEFORE_SURGERY, 'days');
        start = windowOpens.isAfter(tomorrow, 'day') ? windowOpens : tomorrow;
    }

    return firstAcceptableDate(start, date => validateSerologyDate(date, targetSurgeryDate).isValid);
};

// Final XM cannot fall on a Friday, so a Friday shared date shifts back to the Thursday.
const shiftOffFriday = (bloodDrawDate: string): string => {
    if (!bloodDrawDate) {
        return '';
    }

    const date = moment(bloodDrawDate, STORAGE_FORMAT);

    return date.day() === FRIDAY ? date.subtract(1, 'day').format(STORAGE_FORMAT) : bloodDrawDate;
};

// Thursday is preferred so the two draws stay a day apart, but the seed must never be a date the
// field then rejects: fall forward to the next legal Final XM date, or leave it blank.
export const defaultFinalXmDate = (bloodDrawDate: string, targetSurgeryDate: string): string => {
    if (!bloodDrawDate) {
        return '';
    }

    const shifted = shiftOffFriday(bloodDrawDate);

    if (validateFinalXmDate(moment(shifted, STORAGE_FORMAT), targetSurgeryDate).isValid) {
        return shifted;
    }

    return firstAcceptableDate(moment(bloodDrawDate, STORAGE_FORMAT), date => validateFinalXmDate(date, targetSurgeryDate).isValid);
};
